import { FC, useCallback, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { CurrencyIcon, CurrencyEnum } from '@spectrocoin/sc-currencies';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AccountSelect, {
	AccountSelectComponentProps,
} from '../../../../components/AccountSelect/AccountSelect';
import Button, { ButtonType } from '../../../../components/Button/Button';
import MessageBlock, {
	BlockStyle,
	MessageType,
} from '../../../../components/MessageBlock/MessageBlock';
import {
	comparePayCurrencies,
	formatPrecision,
} from '../../../../helpers/currencyHelper/currencyHelper';
import PreorderActions from '../../../../redux/PreorderSlice/PreorderActions';
import { getPaymentOptionsData, getForm } from '../../../../redux/PreorderSlice/PreorderSelectors';
import { useAppDispatch } from '../../../../redux/Store';
import TextNode, { TextStyles } from '../../../../components/TextNode/TextNode';

import styles from './PreorderCurrencySelect.module.scss';
import TestIds, { formatTestId } from '../../../../tests/TestIds';
import { PreorderStage } from '../../../../redux/PreorderSlice/PreorderSlice';
import AmountSummary from '../../../../components/AmountSummary/AmountSummary';

export const messages = defineMessages({
	button: {
		id: 'payments.next',
		defaultMessage: 'Next',
	},
	metaDescription: {
		id: 'payments.preorder_meta',
		defaultMessage: 'Pre-Order information',
	},
	enterAmount: {
		id: 'payments.enter_amount',
		defaultMessage: 'Enter amount',
	},
	selectCryptoForPayment: {
		id: 'payments.select_crypto_for_payment',
		defaultMessage: 'Select crypto for payment',
	},
	paymentInfo: {
		id: 'payments.payment_info',
		defaultMessage:
			'You will have 15 minutes to make your payment. Ensure your wallet is already with sufficient amount of crypto assets.',
	},
	volatilityInfo: {
		id: 'payments.volatility_info',
		defaultMessage:
			'The payment amount may differ slightly due to the volatility of your selected cryptocurrency',
	},
	noPayCurrencyAvailable: {
		id: 'payments.no_pay_currency_available',
		defaultMessage: 'No payment currency availabe for this preorder',
	},
});

const SelectedCurrencyOption = () => {
	const paymentOptions = useSelector(getPaymentOptionsData)!;
	const { payCurrency } = useSelector(getForm)!;

	const { amount: selectedAmount, currencyName: selectedCurrencyName } = useMemo(
		() => paymentOptions.find(({ currencyCode }) => currencyCode === payCurrency)!,
		[paymentOptions, payCurrency]
	);

	return (
		<div className={styles.selectedOption} data-cy={TestIds.PreorderCurrencySelectedCurrency}>
			<CurrencyIcon currencyType={payCurrency!} className={styles.optionIcon} />
			<TextNode style={TextStyles.Ellipsis} className={styles.optionDetails}>
				<div className={styles.optionCurrency}>{selectedCurrencyName}</div>
				<TextNode
					style={TextStyles.Ellipsis | TextStyles.NoWrap}
					className={styles.optionBalance}
				>
					{`${formatPrecision(
						selectedAmount!,
						payCurrency as CurrencyEnum
					)} ${payCurrency}`}
				</TextNode>
			</TextNode>
		</div>
	);
};

const CurrencyOption = ({
	innerProps,
	data: { currencyCode, currencyName: name, amount },
}: AccountSelectComponentProps<{ amount?: string }>) => {
	return (
		<div
			className={styles.option}
			{...innerProps}
			data-cy={formatTestId(TestIds.PreorderCurrencyCurrencyOption_0, currencyCode)}
		>
			<CurrencyIcon currencyType={currencyCode} className={styles.optionIcon} />
			<TextNode style={TextStyles.Ellipsis} className={styles.optionDetails}>
				<div className={styles.optionCurrency}>{name}</div>
				<TextNode
					style={TextStyles.Ellipsis | TextStyles.NoWrap}
					className={styles.optionBalance}
				>
					{`${formatPrecision(amount!, currencyCode as CurrencyEnum)} ${currencyCode}`}
				</TextNode>
			</TextNode>
		</div>
	);
};

const PreorderBody: FC = () => {
	const { formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const paymentOptions = useSelector(getPaymentOptionsData)!;
	const { payCurrency } = useSelector(getForm)!;

	const selectOptions = useMemo(
		() =>
			paymentOptions
				.map(({ amount, currencyCode, currencyName, orderAmount, feeAmount }) => ({
					value: currencyCode,
					currencyCode,
					currencyName,
					amount,
					orderAmount,
					feeAmount,
				}))
				.sort((a, b) => comparePayCurrencies(a.currencyCode, b.currencyCode)),
		[paymentOptions]
	);

	const selectedOption = useMemo(() => {
		return selectOptions.find(({ currencyCode }) => currencyCode === payCurrency);
	}, [payCurrency, selectOptions]);

	const onBack = useCallback(() => {
		dispatch(PreorderActions.setStage(PreorderStage.TravelRule));
	}, []);

	const goToNetworkSelect = useCallback(() => {
		dispatch(PreorderActions.setStage(PreorderStage.NetworkSelect));
	}, []);

	useEffect(() => {
		if (!payCurrency)
			dispatch(PreorderActions.setPayCurrency(selectOptions[0]?.currencyCode || null));
	}, [dispatch, payCurrency, selectOptions]);

	return (
		<div className={styles.body} data-cy={TestIds.Preorder}>
			<div className={styles.currency}>
				<span className={styles.selectCryptoForPayment}>
					{formatMessage(messages.selectCryptoForPayment)}
				</span>
				{payCurrency ? (
					<AccountSelect
						labelClassName={styles.label}
						value={payCurrency}
						options={selectOptions}
						componentOverrides={{
							SingleValue: SelectedCurrencyOption,
							Option: CurrencyOption,
						}}
						onChange={(type) =>
							dispatch(PreorderActions.setPayCurrency(type as CurrencyEnum))
						}
					/>
				) : (
					<MessageBlock
						data-cy={TestIds.PreorderCurrencyNoPaymentOptionsError}
						style={BlockStyle.Fill}
						type={MessageType.Error}
						message={formatMessage(messages.noPayCurrencyAvailable)}
					/>
				)}
				<AmountSummary
					currencyCode={selectedOption?.currencyCode || ''}
					orderAmount={selectedOption?.orderAmount || ''}
					networkFee={selectedOption?.feeAmount || ''}
					totalAmount={selectedOption?.amount || ''}
				/>
				<MessageBlock
					withIcon
					data-cy={TestIds.PreorderCurrencyPaymentInfo}
					message={formatMessage(messages.paymentInfo)}
					type={MessageType.Info}
					style={BlockStyle.Fill}
				/>
			</div>
			<div className={styles.actionButtons}>
				<div className={styles.back} onClick={onBack}>
					<FontAwesomeIcon icon={faChevronLeft} scale={2} />
				</div>
				<Button
					isFullWidth
					data-cy={TestIds.PreorderCurrencyConfirm}
					className={styles.button}
					onClick={goToNetworkSelect}
					text={formatMessage(messages.button)}
					type={ButtonType.BUTTON}
					isDisabled={!payCurrency}
				/>
			</div>
		</div>
	);
};

export default PreorderBody;
